import { DialogContent, IconButton, InputAdornment, TextField, Tooltip, Typography } from '@mui/material'
import Grid from '@mui/material/Grid'

import { PlanValues } from 'api/elplan/elplan.types'
import React, { ReactElement, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import elplanStore from 'store/elplan/elplan'
import { Button } from 'ui/atoms'
import { Dialog } from 'ui/components/Dialog/Dialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import CustomDialogTitle from 'ui/components/SettingsModal/components/CustomDialogTitle/CustomDialogTitle'
import ModalButtons from 'ui/molecules/ModalButtons/ModalButtons'
import Datetime from 'utils/datetime/datetime'
import { formatNumberString } from 'utils/stringUtils'
import { useSnapshot } from 'valtio'
import styles from './EditElectricityPlanModal.module.less'

type ElectricityPlanModalProps = {
  onClose: () => void
  onSubmit: (planValues: PlanValues[]) => void
  planValues: PlanValues[]
  elplanExistsWarningText?: string
  title?: string
}

export default function EditElectricityPlanModal({
  planValues,
  onClose,
  onSubmit,
  title,
  elplanExistsWarningText,
}: ElectricityPlanModalProps): ReactElement {
  const ref = useRef(null)
  const elplanSnap = useSnapshot(elplanStore)
  const [expandedHourIdxs, setExpandedHourIdxs] = useState<number[]>([])
  const [inputMode, setInputMode] = useState<'hourly' | '15-mins'>('hourly')

  const { t } = useTranslation()

  // Group the quarters by hour
  const hourGroups = useMemo(() => {
    const groups: Record<string, PlanValues[]> = {}
    planValues.forEach((q) => {
      const hourStart = new Date(q.start_time)
      hourStart.setUTCMinutes(0, 0, 0)
      const key = hourStart.toISOString()
      if (!groups[key]) groups[key] = []
      groups[key].push(q)
    })

    Object.values(groups).forEach((arr) =>
      arr.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime())
    )
    return groups
  }, [planValues])

  const hourKeys = Object.keys(hourGroups).sort()

  // Track quarter values ready to be sent to endpoint for each hour FOR QUARTER TAB
  const [quarterValues, setQuarterValues] = useState<Record<string, number[]>>(() => {
    const obj: Record<string, number[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume)
    })
    return obj
  })
  // Track quarter values in string format to allow decimals for each hour FOR QUARTER TAB

  const [quarterStringValues, setQuarterStringValues] = useState<Record<string, string[]>>(() => {
    const obj: Record<string, string[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume.toString())
    })
    return obj
  })

  // Track quarter values ready to be sent to endpoint for each hour FOR HOUR TAB
  const [quarterHourValues, setQuarterHourValues] = useState<Record<string, number[]>>(() => {
    const obj: Record<string, number[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume)
    })
    return obj
  })
  // Track quarter values in string format to allow decimals for each hour FOR HOUR TAB
  const [quarterHourStringValues, setQuarterHourStringValues] = useState<Record<string, string[]>>(() => {
    const obj: Record<string, string[]> = {}
    Object.entries(hourGroups).forEach(([hourKey, quarters]) => {
      obj[hourKey] = quarters.map((q) => q.volume.toString())
    })
    return obj
  })
  const rows = useMemo(() => {
    return hourKeys.map((hourKey, i) => {
      const quarters = hourGroups[hourKey]
      const range = `${Datetime.toLocalTime(hourKey as ISODateTime, 'hour')}-${Datetime.toLocalTime(quarters[quarters.length - 1].end_time, 'hour')}`
      // Treat undefined or empty quarter values as zero
      const quarterValsRaw = quarterValues[hourKey] || quarters.map((q) => q.volume)
      const quarterVals = quarterValsRaw.map((v) => {
        if (v === undefined || v === null) return 0
        return Array.isArray(v) ? v[0] : v
      })
      const hourAvgForQuarterInput = (quarterVals.reduce((a, b) => a + b, 0) / quarterVals.length).toFixed(1)

      const hourValueForHourInput = quarterHourStringValues ? quarterHourStringValues[hourKey]?.[0] || 0 : 0 //If the hour has been mixed with from 15-min view, this will look weird
      // In hourly mode, show editable field without expand button
      if (inputMode === 'hourly') {
        return (
          <div
            key={hourKey}
            style={{ display: 'flex', alignItems: 'center', columnGap: 12 }}
            className={styles.SpotBidModal_Row}
          >
            <div className={styles.SpotBidModal_Time}>
              <Typography variant="body2" fontWeight={600} component="span">
                {range}
              </Typography>
            </div>
            <div style={{ flexGrow: 1 }} className={styles.SpotBidModal_InputWrapper}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                value={hourValueForHourInput}
                onChange={(e) => {
                  const valStr = formatNumberString(e.target.value)
                  setQuarterHourStringValues((prev) => {
                    const arr = new Array(quarters.length).fill(valStr ?? '0')
                    return { ...prev, [hourKey]: arr }
                  })
                }}
                onBlur={() => {
                  if (hourValueForHourInput) {
                    const valStr = formatNumberString(hourValueForHourInput)
                    if (valStr === undefined) return
                    setQuarterHourValues((prev) => {
                      const arr = new Array(quarters.length).fill(isNaN(parseFloat(valStr)) ? 0 : parseFloat(valStr))
                      return { ...prev, [hourKey]: arr }
                    })
                  }
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: 'decimal',
                    style: { textAlign: 'left', fontVariantNumeric: 'tabular-nums' },
                  },
                }}
              />
            </div>
          </div>
        )
      }

      // In 15-mins mode, show expandable quarters
      return (
        <React.Fragment key={hourKey}>
          <div style={{ display: 'flex', alignItems: 'center', columnGap: 12 }} className={styles.SpotBidModal_Row}>
            <div className={styles.SpotBidModal_Time}>
              <Typography variant="body2" fontWeight={600} component="span">
                {range}
              </Typography>
            </div>
            <div style={{ flexGrow: 1 }} className={styles.SpotBidModal_InputWrapper}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                disabled
                value={hourAvgForQuarterInput}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Show quarters" arrow>
                        <IconButton
                          size="small"
                          onClick={() => {
                            if (expandedHourIdxs.includes(i)) {
                              setExpandedHourIdxs(expandedHourIdxs.filter((idx) => idx !== i))
                            } else {
                              setExpandedHourIdxs([...expandedHourIdxs, i])
                            }
                          }}
                          aria-label="Show quarters"
                        >
                          <span className="fal fa-chart-pie-alt" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
                slotProps={{
                  htmlInput: {
                    inputMode: 'decimal',
                    style: { textAlign: 'left', fontVariantNumeric: 'tabular-nums' },
                  },
                }}
              />
            </div>
          </div>
          {expandedHourIdxs.includes(i) && (
            <div className="flex flex-col items-end w-full space-y-2 mt-2">
              {quarters.map((q, qIdx) => (
                <div key={q.start_time} style={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="body2" fontWeight={600} component="span" style={{ minWidth: 90 }}>
                    {`${Datetime.toLocalTime(q.start_time, 'hour')}-${Datetime.toLocalTime(q.end_time, 'hour')}`}
                  </Typography>
                  <TextField
                    size="small"
                    variant="outlined"
                    value={
                      quarterStringValues[hourKey]?.[qIdx] === undefined ||
                      quarterStringValues[hourKey]?.[qIdx] === null
                        ? ''
                        : quarterStringValues[hourKey]?.[qIdx]
                    }
                    onChange={(e) => {
                      const valStr = formatNumberString(e.target.value)
                      setQuarterStringValues((prev) => {
                        const arr = prev[hourKey] ? [...prev[hourKey]] : quarters.map((q) => q.volume.toString())
                        arr[qIdx] = valStr ?? '0'
                        return { ...prev, [hourKey]: arr }
                      })
                    }}
                    onBlur={() => {
                      const valStr = quarterStringValues[hourKey]?.[qIdx] || ''
                      setQuarterValues((prev) => {
                        const arr = prev[hourKey] ? [...prev[hourKey]] : quarters.map((q) => q.volume)
                        if (valStr === '') {
                          arr[qIdx] = 0
                        } else {
                          const val = parseFloat(valStr)
                          if (!isNaN(val)) {
                            arr[qIdx] = val
                          }
                        }
                        return { ...prev, [hourKey]: arr }
                      })
                    }}
                    inputProps={{
                      inputMode: 'decimal',
                      style: { textAlign: 'left', fontVariantNumeric: 'tabular-nums' },
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </React.Fragment>
      )
    })
  }, [hourKeys, hourGroups, expandedHourIdxs, quarterValues, inputMode, quarterHourStringValues, quarterStringValues])

  const half = Math.ceil(rows.length / 2)

  return (
    <div className={styles.ElectricityPlanModal}>
      <Dialog open={true} aria-labelledby="form-dialog-title" maxWidth="md" fullWidth container={ref.current}>
        <CustomDialogTitle title={title ?? t('Edit electricity plan')} handleClose={() => onClose()} />
        <DialogContent>
          <InfoBanner
            text={t(
              `Note that you should only edit bids using the same resolution for a day. If you create bids on 15-min intervals and later edit them using hourly intervals, the 15-min bids will be adjusted to match the hourly resolution (and vice versa). Always check your inputs before saving.`
            )}
            style={'warning'}
          />
          <br />
          <div className={styles.ElectricityPlanModal_TableWrapper}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <Typography variant="caption" className={styles.ElectricityPlanModal_Header}>
                <span className={`fal fa-bolt ${styles.ElectricityPlanModal_Table_Icon}`} /> {t('Volume')} (MW)
              </Typography>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button
                  primary={inputMode === 'hourly'}
                  secondary={inputMode !== 'hourly'}
                  onClick={() => {
                    setInputMode('hourly')
                    setExpandedHourIdxs([])
                  }}
                >
                  {t('Hourly')}
                </Button>
                <Button
                  primary={inputMode === '15-mins'}
                  secondary={inputMode !== '15-mins'}
                  onClick={() => setInputMode('15-mins')}
                >
                  {t('15-mins')}
                </Button>
              </div>
            </div>
            <Grid container spacing={2} wrap="nowrap">
              <Grid container size={{ xs: 12, md: 6 }} className={styles.ElectricityPlanModal_Column}>
                {rows.slice(0, half)}
              </Grid>
              <Grid container size={{ xs: 12, md: 6 }} className={styles.ElectricityPlanModal_Column}>
                {rows.slice(half)}
              </Grid>
            </Grid>
          </div>
          {/* Expand/collapse all quarters button and ModalButtons on same row */}
          <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {inputMode === '15-mins' && (
              <Button
                style={{
                  flexShrink: 0,
                }}
                secondary
                icon="fal fa-chart-pie-alt"
                onClick={() => {
                  if (expandedHourIdxs.length === hourKeys.length) {
                    setExpandedHourIdxs([])
                  } else {
                    setExpandedHourIdxs(hourKeys.map((_, i) => i))
                  }
                }}
              >
                {expandedHourIdxs.length === hourKeys.length ? t('Collapse all quarters') : t('Expand all quarters')}
              </Button>
            )}
            {inputMode === 'hourly' && (
              <InfoBanner
                text={t('Even if you enter values per hour, the bid will be saved in quarter-hour resolution.')}
                style="info"
              />
            )}
            <ModalButtons
              submitLabel={inputMode === 'hourly' ? t('Save hourly bid') : t('Save 15-min bid')}
              onSubmit={() => {
                // Build PlanValues[] from quarterValues for submission
                const newPlanValues: PlanValues[] = []
                if (inputMode === 'hourly') {
                  hourKeys.forEach((hourKey) => {
                    const quarters = hourGroups[hourKey]
                    const qArr = quarterHourValues[hourKey] || quarters.map((q) => q.volume)
                    quarters.forEach((q, idx) => {
                      newPlanValues.push({
                        ...q,
                        volume: qArr[0],
                      })
                    })
                  })
                } else {
                  hourKeys.forEach((hourKey) => {
                    const quarters = hourGroups[hourKey]
                    const qArr = quarterValues[hourKey] || quarters.map((q) => q.volume)
                    quarters.forEach((q, idx) => {
                      newPlanValues.push({
                        ...q,
                        volume: qArr[idx],
                      })
                    })
                  })
                }
                onSubmit(newPlanValues)
                onClose()
                return
              }}
              onCancel={() => onClose()}
              infoText={elplanSnap.elplan.has_plan ? elplanExistsWarningText : undefined}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
